import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Plus, Upload, Trash2, FileText, Image as ImageIcon, Video as VideoIcon, Download, ExternalLink, Megaphone,
} from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';
import useHomePath from '@/hooks/useHomePath';

const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const SECTION_COLOR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D4A24B' };

const KIND_ICON = { pdf: FileText, image: ImageIcon, video: VideoIcon };

export default function MarketingMaterials() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [filter, setFilter] = useState(sectionParam && SECTION_LABEL[sectionParam] ? sectionParam : 'all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newMat, setNewMat] = useState({
    title: '', description: '', section: 'probound', kind: 'pdf',
    video_url: '', file: null,
    // Video kind adds a sub-mode: paste a YouTube URL, or upload the file
    // straight to Object Storage. Default to url so existing muscle memory
    // still works.
    video_mode: 'url', // 'url' | 'upload'
  });

  const isStaff = user?.role && ['admin', 'manager', 'team'].includes(user.role);

  const load = async () => {
    setLoading(true);
    const [matRes, meRes] = await Promise.all([
      fetch(`${API_URL}/api/marketing-materials`, { credentials: 'include' }),
      fetch(`${API_URL}/api/auth/me`, { credentials: 'include' }),
    ]);
    setMaterials(matRes.ok ? await matRes.json() : []);
    setUser(meRes.ok ? await meRes.json() : null);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submitNew = async () => {
    // Client-side guard for the two video modes so we surface friendly
    // errors before we start uploading a 500 MB file.
    if (newMat.kind === 'video') {
      if (newMat.video_mode === 'url' && !newMat.video_url.trim()) {
        toast.error('Paste a YouTube / Vimeo URL, or switch to Upload file.');
        return;
      }
      if (newMat.video_mode === 'upload' && !newMat.file) {
        toast.error('Choose a video file to upload, or switch to YouTube link.');
        return;
      }
      if (newMat.video_mode === 'upload' && newMat.file.size > 500 * 1024 * 1024) {
        toast.error(`Video is ${Math.round(newMat.file.size / (1024 * 1024))} MB — max is 500 MB.`);
        return;
      }
    }
    setAdding(true);
    try {
      const params = new URLSearchParams({
        title: newMat.title, description: newMat.description,
        section: newMat.section, kind: newMat.kind,
      });
      // For video kind: send video_url only in URL mode; send file only in upload mode.
      const isVideoUpload = newMat.kind === 'video' && newMat.video_mode === 'upload';
      if (newMat.kind === 'video' && newMat.video_mode === 'url' && newMat.video_url) {
        params.set('video_url', newMat.video_url);
      }
      const fd = new FormData();
      if (newMat.kind !== 'video' && newMat.file) fd.append('file', newMat.file);
      if (isVideoUpload && newMat.file) fd.append('file', newMat.file);
      const res = await fetch(`${API_URL}/api/marketing-materials?${params.toString()}`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Upload failed');
      toast.success('Material added');
      setShowAddModal(false);
      setNewMat({ title: '', description: '', section: 'probound', kind: 'pdf', video_url: '', file: null, video_mode: 'url' });
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    const res = await fetch(`${API_URL}/api/marketing-materials/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) { toast.success('Deleted'); await load(); }
  };

  const filtered = filter === 'all' ? materials : materials.filter(m => m.section === filter);
  // When entering from a section hub (?section=...), lock the page to that section:
  // hide the filter switcher and theme the header to match.
  const isLocked = !!(sectionParam && SECTION_LABEL[sectionParam]);
  const lockedColor = isLocked ? SECTION_COLOR[sectionParam] : null;
  const lockedLabel = isLocked ? SECTION_LABEL[sectionParam] : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="text-white"
        style={isLocked
          ? { background: `linear-gradient(135deg, #0B0B0E 0%, ${lockedColor} 70%)` }
          : { background: 'linear-gradient(135deg, #0c4a6e, #075985)' }}
      >
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-4">
          <button onClick={() => navigate(-1)} data-testid="materials-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl md:text-2xl font-bold flex items-center gap-2 truncate">
              <Megaphone className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
              {isLocked ? `${lockedLabel} · Marketing Material` : 'Marketing Materials'}
            </h1>
            <p className="hidden sm:block text-white/70 text-sm">
              {isStaff
                ? 'Manage the library — uploads are visible to Approved installers in the matching section.'
                : isLocked
                  ? `Branded resources for ${lockedLabel} installers — leaflets, social assets and posters.`
                  : 'Resources to help you market your installation business — gated to your Ox Approved sections.'}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {isStaff && (
              <Button data-testid="materials-add-btn" onClick={() => setShowAddModal(true)} size="sm" className="bg-white text-sky-800 hover:bg-white/90 font-bold px-2 sm:px-3" aria-label="Add Material">
                <Plus className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Add Material</span>
              </Button>
            )}
            <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3" aria-label="Home">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {!isLocked && (
          <div className="flex flex-wrap gap-2 mb-4">
            {['all', 'probound', 'proepoxy', 'prodecor'].map(s => (
              <button
                key={s}
                data-testid={`materials-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${filter === s ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'}`}
              >
                {s === 'all' ? 'All' : SECTION_LABEL[s]}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center text-gray-500">
            {isStaff ? 'No materials yet — click "Add Material" to upload.' : 'No materials available for your Ox Approved sections yet.'}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(m => {
              const Icon = KIND_ICON[m.kind] || FileText;
              return (
                <Card key={m.id} data-testid={`material-${m.id}`} className="overflow-hidden">
                  <div className="p-3 text-white flex items-center gap-2" style={{ background: SECTION_COLOR[m.section] }}>
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">{SECTION_LABEL[m.section]} · {m.kind}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900">{m.title}</h3>
                    {m.description && <p className="text-sm text-gray-600 mt-1">{m.description}</p>}
                    <div className="mt-4 flex gap-2 flex-wrap">
                      {m.kind === 'video' ? (
                        m.storage_path ? (
                          // Uploaded video → open the auth-gated stream endpoint
                          // in a new tab. Browser's native player will handle it.
                          <a
                            href={`${API_URL}/api/marketing-materials/${m.id}/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-sky-700 text-white hover:bg-sky-800"
                            data-testid={`material-watch-${m.id}`}
                          >
                            <VideoIcon className="w-3 h-3" /> Play
                          </a>
                        ) : (
                          <a
                            href={m.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-sky-700 text-white hover:bg-sky-800"
                            data-testid={`material-watch-${m.id}`}
                          >
                            <ExternalLink className="w-3 h-3" /> Watch
                          </a>
                        )
                      ) : (
                        <a
                          href={`${API_URL}/api/marketing-materials/${m.id}/download`}
                          className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-sky-700 text-white hover:bg-sky-800"
                          data-testid={`material-download-${m.id}`}
                        >
                          <Download className="w-3 h-3" /> Download
                        </a>
                      )}
                      {isStaff && (
                        <button
                          onClick={() => remove(m.id)}
                          className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md text-rose-700 hover:bg-rose-50"
                          data-testid={`material-delete-${m.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Megaphone className="w-5 h-5" /> Add Material</h2>
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input data-testid="new-mat-title" value={newMat.title} onChange={(e) => setNewMat({ ...newMat, title: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="new-mat-desc" rows={2} value={newMat.description} onChange={(e) => setNewMat({ ...newMat, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Section</Label>
                  <select data-testid="new-mat-section" value={newMat.section} onChange={(e) => setNewMat({ ...newMat, section: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                    <option value="probound">ProBound</option>
                    <option value="proepoxy">ProEpoxy</option>
                    <option value="prodecor">ProDecor</option>
                  </select>
                </div>
                <div>
                  <Label>Type</Label>
                  <select data-testid="new-mat-kind" value={newMat.kind} onChange={(e) => setNewMat({ ...newMat, kind: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                    <option value="pdf">PDF</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
              </div>
              {newMat.kind === 'video' ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewMat({ ...newMat, video_mode: 'url', file: null })}
                      className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium ${
                        newMat.video_mode === 'url' ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid="new-mat-mode-url"
                    >
                      Paste YouTube link
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewMat({ ...newMat, video_mode: 'upload', video_url: '' })}
                      className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium ${
                        newMat.video_mode === 'upload' ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid="new-mat-mode-upload"
                    >
                      Upload file
                    </button>
                  </div>
                  {newMat.video_mode === 'url' ? (
                    <div>
                      <Label>Video URL (YouTube / Vimeo)</Label>
                      <Input
                        data-testid="new-mat-url"
                        value={newMat.video_url}
                        onChange={(e) => setNewMat({ ...newMat, video_url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                    </div>
                  ) : (
                    <div>
                      <Label>Video file (mp4, mov, webm — max 500MB)</Label>
                      <Input
                        data-testid="new-mat-video-file"
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
                        onChange={(e) => setNewMat({ ...newMat, file: e.target.files?.[0] })}
                      />
                      {newMat.file && (
                        <p className="text-xs text-gray-500 mt-1">
                          {newMat.file.name} · {(newMat.file.size / (1024 * 1024)).toFixed(1)} MB
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <Label>File (max 25MB)</Label>
                  <Input data-testid="new-mat-file" type="file" accept={newMat.kind === 'pdf' ? 'application/pdf' : 'image/*'} onChange={(e) => setNewMat({ ...newMat, file: e.target.files?.[0] })} />
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button data-testid="new-mat-submit" onClick={submitNew} disabled={adding || !newMat.title} className="bg-sky-700 hover:bg-sky-800 text-white">
                  <Upload className="w-4 h-4 mr-1" /> {adding ? 'Uploading…' : 'Add'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
